import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { APIList, AttachmentStatus } from 'src/interfaces'
import { Entities } from 'src/enums'
import { Attachment } from 'src/api'
import { useApiInstance } from './useApiInstance'
import {
  buildRequestParams,
  ObjectType,
  transformPage,
  transformPerPage,
} from './helpers'

interface RequestParams {
  params?: Partial<AdminAttachmentListGetParams>;
  paramsTransform?: Array<(params: Record<string, any>) => Record<string, any>>;
  options?: Omit<
    UseQueryOptions<unknown, Error, APIList<Attachment>>,
    'queryKey' | 'queryFn'
  >;
}

interface AdminAttachmentListGetParams {
  limit: number;
  offset: number;
  sort: string;
  attachmentStatus: AttachmentStatus;
}

const schema: ObjectType['schema'] = {
  limit: 'number',
  offset: 'number',
  sort: 'string',
  attachmentStatus: 'string',
}

export const useAttachmentListRequest = ({
  params = {},
  paramsTransform = [],
  options,
}: RequestParams = {}) => {
  const api = useApiInstance()

  const transformedParams = [
    transformPerPage,
    transformPage,
    ...paramsTransform,
  ].reduce((param, transform) => transform(param), params)

  const queryParams = buildRequestParams<AdminAttachmentListGetParams>(
    transformedParams,
    schema,
  )

  return useQuery<unknown, Error, APIList<Attachment>>(
    [Entities.Attachments, JSON.stringify(transformedParams)],
    async () => {
      const { limit, offset, sort, attachmentStatus } = queryParams
      const { data } = await api.v1AdminAttachmentsGet(
        limit,
        offset,
        sort,
        attachmentStatus,
      )
      return data
    },
    options,
  )
}
